<template>
  <section class="bg-gradient-to-b from-base-100 to-base-50 py-12 md:py-16">
    <!-- Subtle background effects -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-1/3 left-1/4 w-48 h-48 bg-gradient-to-r from-success/4 to-primary/4 rounded-full blur-3xl"></div>
      <div
        class="absolute bottom-1/4 right-1/3 w-64 h-64 bg-gradient-to-r from-primary/4 to-secondary/4 rounded-full blur-2xl"></div>
    </div>

    <div class="relative z-10 max-w-6xl mx-auto px-4">
      <!-- Professional header -->
      <div class="text-center mb-12">
        <div class="relative inline-block mb-6">
          <h2 class="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            <span class="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-base-content">
              {{ $t("socialProofEnhanced.heading") }}
            </span>
          </h2>
          <!-- Simple underline -->
          <div
            class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-success to-secondary rounded-full"></div>
        </div>

        <p class="text-xl text-base-content/80 leading-relaxed max-w-3xl mx-auto">
          {{ $t("socialProofEnhanced.subtitle") }}
        </p>
      </div>

      <div class="grid lg:grid-cols-2 gap-8 lg:gap-10 mb-12">
        <div v-for="index in storiesCount" :key="index" class="group relative">
          <div
            class="relative bg-white border border-base-200 shadow-lg hover:shadow-xl rounded-2xl p-8 transition-all duration-300 h-full">
            <div
              :class="[
                'absolute inset-0 rounded-2xl pointer-events-none',
                index === 1
                  ? 'bg-gradient-to-br from-error/3 via-primary/5 to-success/3'
                  : 'bg-gradient-to-br from-secondary/3 via-success/5 to-primary/3',
              ]"></div>

            <div class="relative z-10">
              <div class="mb-8">
                <div
                  class="inline-flex items-center gap-3 bg-gradient-to-r from-error/10 to-error/5 border border-error/20 rounded-xl px-4 py-2 mb-4">
                  <span class="text-error font-semibold text-sm uppercase tracking-wide">{{
                    $t("socialProofEnhanced.problemTitle")
                  }}</span>
                </div>
                <p class="text-lg text-error/90 leading-relaxed font-medium italic">
                  {{ $t(`socialProofEnhanced.stories.${index - 1}.problem`) }}
                </p>
              </div>

              <!-- Solution testimonial -->
              <div class="mb-8">
                <div
                  :class="[
                    'relative border-l-4 rounded-r-xl p-6',
                    index === 1
                      ? 'bg-gradient-to-br from-primary/5 via-primary/8 to-primary/5 border-primary'
                      : 'bg-gradient-to-br from-secondary/5 via-secondary/8 to-secondary/5 border-secondary',
                  ]">
                  <p class="text-lg text-base-content/90 leading-relaxed font-medium relative z-10 mb-4">
                    {{ $t(`socialProofEnhanced.stories.${index - 1}.solution`) }}
                  </p>
                  <cite
                    :class="[
                      'font-bold text-lg not-italic flex items-center gap-2',
                      index === 1 ? 'text-primary' : 'text-secondary',
                    ]">
                    <div :class="['w-1 h-6 rounded-full', index === 1 ? 'bg-primary' : 'bg-secondary']"></div>
                    {{ $t(`socialProofEnhanced.stories.${index - 1}.author`) }}
                  </cite>
                </div>
              </div>

              <!-- Result badge -->
              <div
                class="inline-flex items-center gap-3 bg-gradient-to-r from-success/15 to-success/8 border border-success/30 rounded-xl px-6 py-3">
                <span class="text-success font-bold text-base">
                  {{ $t(`socialProofEnhanced.stories.${index - 1}.result`) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="relative mb-20">
        <div class="group relative bg-white border border-base-200 shadow-lg hover:shadow-xl rounded-2xl p-12">
          <div
            class="absolute inset-0 bg-gradient-to-br from-primary/5 via-success/8 to-secondary/5 rounded-2xl pointer-events-none"></div>

          <div class="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div class="group/stat relative">
              <div class="mb-4">
                <span class="text-5xl lg:text-6xl font-bold text-primary"> 100+ </span>
              </div>
              <span class="text-lg font-semibold text-base-content/80">{{
                $t("socialProofEnhanced.stats.websitesHelped")
              }}</span>
            </div>

            <div class="group/stat relative">
              <div class="mb-4">
                <span class="text-5xl lg:text-6xl font-bold text-success">
                  {{ $t("socialProofEnhanced.stats.averageLaunchTimeNumber") }}
                </span>
              </div>
              <span class="text-lg font-semibold text-base-content/80">{{
                $t("socialProofEnhanced.stats.averageLaunchTime")
              }}</span>
            </div>

            <div class="group/stat relative">
              <div class="mb-4">
                <span class="text-5xl lg:text-6xl font-bold text-secondary"> 96.2% </span>
              </div>
              <span class="text-lg font-semibold text-base-content/80">{{
                $t("socialProofEnhanced.stats.clientSatisfaction")
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="text-center">
        <p class="text-lg font-medium text-base-content/70 mb-4">
          {{ $t("socialProofEnhanced.logoCaption") }}
        </p>

        <!-- Logo showcase -->
        <div class="flex gap-8 lg:gap-12 justify-center flex-wrap items-center">
          <div v-for="logo in logos" :key="logo" class="group relative">
            <div
              class="relative p-4 bg-white border border-base-200 rounded-xl shadow-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300">
              <img
                :src="logo"
                alt="Client logo"
                class="h-12 lg:h-16 opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-300" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { useI18n } from "vue-i18n";
import clientArtn from "/images/clients/artn.png";
import clientBambooGym from "/images/clients/bamboogym.png";
import clientCateringVanAlex from "/images/clients/cateringvanalex.avif";
import fysioBoutique from "/images/clients/defysioboutique.jpg";

export default defineComponent({
  name: "SocialProofSection",
  setup() {
    const { locale, messages } = useI18n();

    const testimonials = computed(
      () => ((messages.value?.[locale.value] as any)?.social?.testimonials as string[]) || []
    );

    const storiesCount = computed(
      () => ((messages.value?.[locale.value] as any)?.socialProofEnhanced?.stories?.length as number) || 0
    );

    const logos = [clientArtn, clientBambooGym, clientCateringVanAlex, fysioBoutique];
    return { testimonials, storiesCount, logos };
  },
});
</script>
